import axios from '../axiosConfig';

export type FilePreviewKind = 'pdf' | 'image' | 'excel' | 'unsupported';

export interface FilePreviewResource {
  filename: string;
  blob: Blob;
  mimeType: string;
  extension: string;
  kind: FilePreviewKind;
}

function resolveUploadedFileName(data: any): string | null {
  if (typeof data === 'string' && data.trim()) {
    return data.trim();
  }

  const fileName = data?.fileName || data?.id || data?.name || null;
  if (typeof fileName === 'string' && fileName.trim()) {
    return fileName.trim();
  }

  return null;
}

export async function uploadFile(file: File): Promise<string> {
  const form = new FormData();
  form.append('file', file);

  const { data } = await axios.post('/File', form);
  const uploadedFileName = resolveUploadedFileName(data);

  if (!uploadedFileName) {
    throw new Error('Invalid upload response');
  }

  return uploadedFileName;
}

export async function downloadFile(filename: string): Promise<Blob> {
  const { data } = await axios.get(`/File/${encodeURIComponent(filename)}`, { responseType: 'blob' });
  return data;
}

export function getFileExtension(filename: string): string {
  const normalized = String(filename || '').trim().toLowerCase();
  const dotIndex = normalized.lastIndexOf('.');
  return dotIndex >= 0 ? normalized.slice(dotIndex + 1) : '';
}

export function getFilePreviewKind(filename: string, mimeType?: string | null): FilePreviewKind {
  const extension = getFileExtension(filename);
  const normalizedMimeType = String(mimeType || '').trim().toLowerCase();

  if (normalizedMimeType.includes('pdf') || extension === 'pdf') {
    return 'pdf';
  }

  if (
    normalizedMimeType.startsWith('image/') ||
    ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg'].includes(extension)
  ) {
    return 'image';
  }

  if (
    normalizedMimeType.includes('spreadsheet') ||
    normalizedMimeType.includes('excel') ||
    ['xls', 'xlsx', 'xlsm', 'xlsb'].includes(extension)
  ) {
    return 'excel';
  }

  return 'unsupported';
}

export async function fetchFileForPreview(filename: string): Promise<FilePreviewResource> {
  const blob = await downloadFile(filename);
  const mimeType = String(blob.type || '').trim();

  return {
    filename,
    blob,
    mimeType,
    extension: getFileExtension(filename),
    kind: getFilePreviewKind(filename, mimeType),
  };
}
